import widgets from "@/components";
import plugins from "@/plugins";
import { logger } from "@/helpers/logger";
import {
  FrontgateWSConnection,
  Mdg2Client,
  TokenAuthentication,
} from "@fds/wm-typescript-mdg2-client";
import Vue from "vue";
import store from "@/store";
import "@/styles/index.scss";

Vue.config.productionTip = false;

widgets.forEach((widget) => {
  Vue.component(widget.name, widget.widget);
});

export default class {
  mdg2Client;

  private readonly vueInstances: Record<string, Vue> = {};

  public constructor(mdg2Client: Mdg2Client) {
    this.vueInstances = {};
    this.mdg2Client = mdg2Client;
  }

  getEnv() {
    return process.env.VUE_APP_ENV;
  }

  public destroy(elementId: string): void {
    const rootElement = document.createElement("div");
    rootElement.setAttribute("id", elementId);
    const element = this.vueInstances[elementId].$el;
    this.vueInstances[elementId].$destroy();
    element.replaceWith(rootElement);
  }

  public destroyAll(): void {
    Object.keys(this.vueInstances).forEach((elementId) => {
      this.destroy(elementId);
    });
  }

  public async init(
    elementId: string,
    chartSourcePrefix = ""
  ): Promise<boolean> {
    const { mdg2Client } = this;

    Vue.mixin({
      data: () => ({
        get mdg2Client() {
          return mdg2Client;
        },
        get chartSourcePrefix() {
          return chartSourcePrefix;
        },
      }),
    });

    try {
      plugins.forEach((plugin) => {
        const { name, options } = plugin(this.mdg2Client);
        Vue.use(name, options);
      });
      this.vueInstances[elementId] = new Vue({
        el: elementId,
        store,
      });

      return true;
    } catch (error: unknown) {
      logger.error(error);
      return false;
    }
  }

  static async getToken(tokenProvider?: string) {
    if (tokenProvider === undefined) {
      if (process.env.VUE_APP_TOKEN_PROVIDER) {
        tokenProvider = process.env.VUE_APP_TOKEN_PROVIDER;
      } else {
        throw new Error(
          "[VUE_APP_TOKEN_PROVIDER] environment variable not set."
        );
      }
    }

    const res = await fetch(tokenProvider);
    await res.json().then((tokenProviderResponse) => {
      const token = tokenProviderResponse.token;
      const tokenRetrievedEvent = new CustomEvent("token-retrieved", {
        detail: token,
      });
      window.dispatchEvent(tokenRetrievedEvent);
    });
  }

  static async mdg2ClientConnect(token: string, wsHost?: string) {
    let host: string;
    const auth = new TokenAuthentication(token);

    if (process.env.VUE_APP_FRONTGATE_HOST) {
      host = process.env.VUE_APP_FRONTGATE_HOST;
    } else {
      throw new Error("[VUE_APP_FRONTGATE_HOST] environment variable not set.");
    }
    const options = {
      host: host,
    };
    if (wsHost) {
      options.host = wsHost;
    }

    const connection = new FrontgateWSConnection(auth, options);
    const mdg2Client = new Mdg2Client(connection);

    try {
      await mdg2Client.connect();
      if (process.env.VUE_APP_ENV !== "prod") {
        logger.info("mdg2client connected");
      }
    } catch (err) {
      if (process.env.VUE_APP_ENV !== "prod") {
        logger.error("mdg2client could not connect", err);
      }
    }

    return mdg2Client;
  }

  /**
   * Get the chart instance with only the functions you want to expose
   */
  getChart(elementId: string) {
    const chartIndex = (this as any).vueInstances[
      elementId
    ].$children.findIndex(
      (vueInstance: { $el: { className: string } }) =>
        vueInstance.$el.className === "egb-chart"
    );
    const chartVueInstance = (this as any).vueInstances[elementId].$children[
      chartIndex
    ];
    return {
      addBenchmark: chartVueInstance.addBenchmark,
      getBenchmarks: chartVueInstance.getBenchmarks,
      deleteBenchmark: chartVueInstance.deleteBenchmark,
      changeTheme: chartVueInstance.changeTheme,
      changeLanguage: chartVueInstance.changeLanguage,
      setRange: chartVueInstance.setRange,
      getNotation: chartVueInstance.getNotation,
      setNotation: chartVueInstance.setNotation,
      reloadChart: chartVueInstance.reloadChart,
    };
  }
}
